<template>
  <b-tab active @click="refreshTable()" data-me="master-fr-tag-tab">
    <template #title>
      <span class="d-none d-sm-inline-block">
        {{ $t('master.list.rfid.tag.title') }}
      </span>
    </template>
    <div v-if="isTableDataAvailable" class="d-flex">
      <b-col md="auto">
        <span class="fw-light mc-row-check mt-0">
          {{ $t('master.list.rfid.tag.RFIDActive') }}
        </span>
        <div class="fw-bold mc-strong__label">
          {{ totalActiveRFID }}
        </div>
      </b-col>
      <b-col md="auto" class="mx-3">
        <span class="fw-light mc-row-check mt-0">
          {{ $t('master.list.rfid.tag.RFIDActiveWithoutDriver') }}
        </span>
        <div class="fw-bold mc-strong__label">
          {{ totalActiveRFIDWithoutDriver }}
        </div>
      </b-col>
      <b-col>
        <span class="fw-light mc-row-check mt-0">
          {{ $t('master.list.rfid.tag.RFIDInactive') }}
        </span>
        <div class="fw-bold mc-strong__label">
          {{ totalInactiveRFID }}
        </div>
      </b-col>
    </div>
    <!-- Table -->
    <div class="table-responsive mc-tab__rfid mb-5">
      <McGrapqlTable
        v-if="isTableDataAvailable"
        ref="table-ref"
        :fields="fields"
        :apiClient="query"
        :searchable="true"
        :hasDelete="false"
        :withFlex="false"
        trackBy="rfid"
        :hasCheckbox="false"
        data-me="master-fr-tag-table"
      >
        <!-- Slot: Status Column -->
        <template #cell(isActive)="{ item }">
          <span v-if="item.isActive.value == 'A'" class="mc-status mc-status--green">
            {{ $t('global.active') }}
          </span>
          <span v-else class="mc-status mc-status--red">{{ $t('global.nonActive') }}</span>
        </template>

        <!-- Slot: Change History Column -->
        <template #cell(history)="{ item }">
          <McButton
            data-me="master-fr-tag-btn-history"
            variant="secondary"
            :text="$t('master.list.rfid.tag.history')"
            class="mc-btn__sm mc-btn__table d-flex justify-content-center"
            @click="handleShowHistory(item)"
          />
        </template>

        <!-- Slot: Assigned Date Driver Column -->
        <template #cell(assignedDate)="{ item }">
          <p class="my-0">
            {{ item.assignedDate ? formatIndexDate(item.assignedDate).date : '-' }}
          </p>
          <p v-if="item.assignedDate" class="text-muted my-0">
            {{ formatIndexDate(item.assignedDate).time }}
          </p>
        </template>
      </McGrapqlTable>

      <!-- Empty State -->
      <div v-show="!isTableDataAvailable" class="empty-table-container">
        <McEmptyState
          data-me="master-fr-tag-empty-state"
          :title="$t('master.list.rfid.tag.emptyState')"
          :desc="$t('master.list.rfid.tag.emptyStateDesc')"
          :hasButton="false"
          class="pb-4"
        />
      </div>
    </div>
  </b-tab>
</template>
<script>
import { mapGetters } from 'vuex'
import McGrapqlTable from '@/components/McGrapqlTable'
import McButton from '@/components/McButton'
import McEmptyState from '@/components/McEmptyState'
import { Helper } from '@/mixin/helper'
import { formatIndexDate } from '@/helpers/tzDate'
import { GET_RFIDS } from '@/services/api/master/rfid'
export default {
  components: {
    McGrapqlTable,
    McButton,
    McEmptyState,
  },
  mixins: [Helper],
  data() {
    return {
      totalActiveRFID: 0,
      totalActiveRFIDWithoutDriver: 0,
      totalInactiveRFID: 0,
      isTableDataAvailable: true,
      listRFIDTags: [],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    fields() {
      return [
        {
          key: 'rfid',
          label: this.$t('master.list.rfid.tag.RFIDNumber'),
          thClass: 'mc-table--50',
          isSortable: true,
        },
        {
          key: 'isActive',
          label: this.$t('master.list.rfid.tag.status'),
          thClass: 'mc-table--170',
          isSortable: true,
          customColumn: true,
        },
        {
          key: 'driver.fullname',
          label: this.$t('master.list.rfid.tag.driver'),
          thClass: 'mc-table--170',
          isSortable: true,
          formatter: (item) => item || '-',
        },
        {
          key: 'description',
          label: this.$t('master.list.rfid.tag.description'),
          thClass: 'mc-table--50',
          isSortable: false,
          formatter: (item) => item || '-',
        },
        {
          key: 'assignedDate',
          label: this.$t('master.list.rfid.tag.assignedDate'),
          thClass: 'mc-table--50',
          isSortable: true,
          customColumn: true,
        },
      ]
    },
    query() {
      return {
        get: async (opts) => {
          const res = await this.$apollo.query({
            query: GET_RFIDS,
            variables: {
              filter: {
                orderBy: opts.orderBy,
                search: opts.search,
                pagination: {
                  page: opts.pagination.page,
                  show: opts.pagination.show,
                },
              },
            },
            fetchPolicy: 'no-cache',
          })

          res.data.getRfids.getRfids.data = res.data.getRfids.getRfids.data.map((item) => {
            return {
              ...item,
              driver: item.driverId
                ? {
                    userId: item.driverId || null,
                    fullname: item.driver?.fullname || '',
                    isActive: item.driver?.isActive,
                  }
                : null,
              isActive: {
                value: item.isActive ? 'A' : 'I',
                name: item.isActive ? this.$t('global.active') : this.$t('global.nonActive'),
              },
            }
          })
          this.totalActiveRFID = res.data.getRfids.getRfids.rfidStatistic.activated
          this.totalActiveRFIDWithoutDriver = res.data.getRfids.getRfids.rfidStatistic.notAssigned
          this.totalInactiveRFID = res.data.getRfids.getRfids.rfidStatistic.inactivated
          if (
            res.data.getRfids.getRfids.data.length === 0 &&
            (opts.search === '' || opts.search === undefined)
          ) {
            this.isTableDataAvailable = false
          }
          return res.data.getRfids.getRfids
        },
      }
    },
  },
  mounted() {
    this.getAllRFIDTags()
  },
  methods: {
    formatIndexDate,
    refreshTable() {
      this.getAllRFIDTags()

      if (this.$refs['table-ref']) {
        this.$refs['table-ref'].handleUnselectAll()
        this.$refs['table-ref'].getData({
          limit: this.$refs['table-ref'].limit,
          page: 1,
          sorts: this.$refs['table-ref'].stringSorts(),
        })
      }
    },
    async getAllRFIDTags() {
      const res = await this.$apollo.query({
        query: GET_RFIDS,
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        },
        fetchPolicy: 'no-cache',
      })
      this.listRFIDTags = res.data.getRfids.getRfids.data
    },

    handleShowHistory(item) {
      return this.$router.push({
        name: 'Master RFID History Tag',
        params: { id: item.rfid },
      })
    },
  },
}
</script>
